// @flow
import React, { useState, useRef, useCallback } from "react";
import moment from "moment-timezone";
import bem from "@justpark/helpers/src/bem/bem";
import InputBox from "../InputBox";
import ChevronIcon from "../Icons/ChevronIcon";
import LazySingleDatePicker from "../LazySingleDatePicker";
import useOutsideClickHandler from "../../hooks/useOutsideClickHandler";
import useNativeInputs from "../../hooks/useNativeInputs";
import Typography from "../Typography";

import "./singleDatePickerDropDown.scss";

type Props = {
  date: ?string,
  error: string,
  hideErrorCross: boolean,
  onChange: (string) => void,
  className?: string,
  disabled?: boolean,
  max?: string | null,
  min?: string | null,
  placeholder?: string,
  label?: string,
  labelColor?: string
};

const baseClass = "c-single-date-picker-drop-down";

const SingleDatePickerDropDown = ({
  date,
  onChange,
  className = "",
  error,
  hideErrorCross,
  disabled = false,
  min = moment().format("YYYY-MM-DD"),
  max = null,
  placeholder = "Select start date",
  label = "",
  labelColor = "light"
}: Props) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const node = useRef();
  const inputRef = useRef();

  const onClose = useCallback(() => setShowDatePicker(false), []);

  useOutsideClickHandler(node, onClose, showDatePicker);

  const useNative = useNativeInputs();

  const formattedDate = date ? moment(date).format("Do MMM YYYY") : "";

  const onClickEvent = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  const onFancyInputClick = useCallback((event: MouseEvent) => {
    event.target.blur();
    event.stopPropagation();
    event.preventDefault();
    setShowDatePicker(true);
  }, []);

  const isOutsideRange = useCallback(
    (day) => {
      const isBefore = min ? day.isBefore(moment(min), "day") : false;
      const isAfter = max ? day.isAfter(moment(max), "day") : false;

      return isBefore || isAfter;
    },
    [min, max]
  );

  return (
    <div ref={node} className={`${baseClass} ${className}`}>
      {label && (
        <label className={bem(baseClass, "label")}>
          <Typography size="secondary" bold color={labelColor}>
            <div className={bem(baseClass, "labeltext")}>{label}</div>
          </Typography>
        </label>
      )}

      <div className={bem(baseClass, "date-input-wrapper")}>
        {useNative && (
          <input
            type="date"
            className={bem(baseClass, "date-input")}
            value={date || ""}
            onClick={onClickEvent}
            ref={inputRef}
            min={min}
            max={max}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        )}

        <InputBox
          className={bem(baseClass, "date")}
          value={formattedDate}
          placeholder={placeholder}
          icon={<ChevronIcon />}
          hideErrorCross={hideErrorCross}
          onClick={onFancyInputClick}
          onFocus={onFancyInputClick}
          error={error}
          disabled={disabled}
          onChange={() => {}}
        />
      </div>

      {showDatePicker && (
        <LazySingleDatePicker
          id="single-date-picker-dropdown"
          date={date ? moment(date) : moment().add("1", "day")}
          onChange={(momentValue) => {
            const dateValue = momentValue.format("YYYY-MM-DD");
            onChange(dateValue);
            setShowDatePicker(false);
          }}
          isOutsideRange={isOutsideRange}
        />
      )}
    </div>
  );
};

export default SingleDatePickerDropDown;
